/**
 * Portapapeles de campos del editor de flujos.
 *
 * Permite configurar un campo una sola vez y pegarlo en otra sección u otro nodo, en lugar de
 * volver a capturar sus ~86 propiedades a mano. Es intencional que el id se conserve al pegar:
 * los valores se guardan en cotizacionesDetalle por `campo`, así que repetir el id entre nodos
 * es justamente lo que hace que el dato capturado en un nodo se vea en el siguiente.
 *
 * Vive en localStorage para sobrevivir a un F5 y permitir copiar de un flujo y pegar en otro,
 * incluso de otro producto — por eso se guardan también los catálogos locales que el campo
 * referencia, que allá podrían no existir.
 *
 * Por lo mismo caduca: a las 24 horas (ver VIGENCIA_MS), y además se limpia cuando cambia el
 * usuario logueado (ver la mutación authSetInfo en store/index.js). Sin eso, el botón de pegar
 * aparecía con algo copiado en otra sesión, o por otra persona en la misma máquina.
 */

const LLAVE = 'cw_campo_clipboard';
const VERSION = 1;

/**
 * Lo copiado caduca a las 24 horas. Sin esto, al vivir en localStorage, el botón de
 * pegar reaparece días después ofreciendo un campo de un flujo que ya no se recuerda.
 */
const VIGENCIA_MS = 24 * 60 * 60 * 1000;

/** Copia profunda sin arrastrar referencias reactivas de Vue. */
function clonar(valor) {
    return JSON.parse(JSON.stringify(valor));
}

/**
 * Guarda un campo en el portapapeles.
 *
 * @param {object} campo      el campo completo
 * @param {object} contexto   {productoId, nodoNombre, catalogos: [{slug, nombreCatalogo, items}]}
 * @returns {boolean}
 */
export function copiarCampo(campo, contexto = {}) {
    if (!campo || typeof campo !== 'object') return false;

    try {
        localStorage.setItem(LLAVE, JSON.stringify({
            v: VERSION,
            copiadoEn: Date.now(),
            campo: clonar(campo),
            productoId: contexto.productoId || 0,
            nodoNombre: contexto.nodoNombre || '',
            catalogos: clonar(contexto.catalogos || []),
        }));
        return true;
    } catch (e) {
        // localStorage lleno o bloqueado: no es fatal, simplemente no se copia
        return false;
    }
}

/** Contenido del portapapeles, o null si está vacío o es de una versión que ya no entendemos. */
export function leerPortapapeles() {
    let crudo;
    try {
        crudo = localStorage.getItem(LLAVE);
    } catch (e) {
        return null;
    }
    if (!crudo) return null;

    let datos;
    try {
        datos = JSON.parse(crudo);
    } catch (e) {
        return null;
    }

    if (!datos || datos.v !== VERSION || !datos.campo) return null;

    // Vencido: se descarta y además se borra, para no volver a evaluarlo en cada lectura.
    // Lo copiado antes de que existiera `copiadoEn` se trata como vencido.
    if (!datos.copiadoEn || (Date.now() - datos.copiadoEn) > VIGENCIA_MS) {
        limpiarPortapapeles();
        return null;
    }

    return datos;
}

export function limpiarPortapapeles() {
    try {
        localStorage.removeItem(LLAVE);
    } catch (e) { /* nada que hacer */ }
}

/** Etiqueta corta para el botón de pegar. */
export function etiquetaPortapapeles(datos) {
    if (!datos || !datos.campo) return '';
    const campo = datos.campo;
    return (campo.nombre || '').trim() || campo.id || 'campo sin nombre';
}

/**
 * Ids de campo que ya existen en una sección.
 * @param {object} seccion
 */
function idsDeSeccion(seccion) {
    return ((seccion && seccion.campos) || [])
        .map(function (c) { return c && c.id; })
        .filter(Boolean);
}

/**
 * Ids de campo presentes en un nodo completo (todas sus secciones).
 * @param {object} nodo
 */
function idsDeNodo(nodo) {
    const ids = [];
    const secciones = (nodo && nodo.formulario && nodo.formulario.secciones) || [];
    secciones.forEach(function (sec) {
        idsDeSeccion(sec).forEach(function (id) { ids.push(id); });
    });
    return ids;
}

/**
 * Revisa un pegado antes de ejecutarlo.
 *
 * @param {object} campo    el campo que se va a pegar
 * @param {object} seccion  sección destino
 * @param {object} nodo     nodo destino
 * @returns {{colision: boolean, dependenciasFaltantes: string[]}}
 */
export function revisarPegado(campo, seccion, nodo) {
    const colision = idsDeSeccion(seccion).indexOf(campo.id) !== -1;

    // Un campo condicionado a otro que no está en el nodo destino no falla de forma visible:
    // simplemente no se muestra nunca. Conviene avisarlo antes que dejarlo pasar callado.
    const presentes = idsDeNodo(nodo);
    const faltantes = [];

    (campo.dependOn || []).forEach(function (dep) {
        const id = dep && dep.campoId;
        if (!id) return;
        if (presentes.indexOf(id) === -1 && faltantes.indexOf(id) === -1) faltantes.push(id);
    });

    // dependAdv es una expresión libre; se buscan los ids conocidos que menciona
    const avanzada = (campo.dependAdv || '').toString();
    if (avanzada.trim() !== '') {
        (avanzada.match(/[A-Za-z_][A-Za-z0-9_]*/g) || []).forEach(function (token) {
            // solo se reporta lo que parece un campo del flujo y no está en el nodo destino
            if (token === campo.id) return;
            if (presentes.indexOf(token) !== -1) return;
            if (faltantes.indexOf(token) !== -1) return;
            if (token.length < 4) return;
            faltantes.push(token);
        });
    }

    return {colision: colision, dependenciasFaltantes: faltantes};
}

/**
 * Devuelve una copia del campo lista para insertarse.
 *
 * @param {object} campo
 * @param {string} idNuevo  si viene, reemplaza el id (caso colisión)
 */
export function prepararCampoPegado(campo, idNuevo) {
    const copia = clonar(campo);
    if (idNuevo) copia.id = idNuevo;
    copia.opened = true;
    // La pertenencia a un grupo es propia de la sección de origen: el grupo no existe
    // en la sección donde se pega, así que el campo llega suelto.
    copia.grupoId = '';
    return copia;
}

/**
 * Sugiere un id libre dentro de la sección, agregando _2, _3… al original.
 */
export function idLibreEnSeccion(idBase, seccion) {
    const existentes = idsDeSeccion(seccion);
    if (existentes.indexOf(idBase) === -1) return idBase;

    let n = 2;
    while (existentes.indexOf(idBase + '_' + n) !== -1) n++;
    return idBase + '_' + n;
}
